import React, { useState, useEffect } from 'react';
import { auth, db } from '../config/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../contexts/UserProfileContext'; // Import context
import { getIdleLimit, getLastActivity, markActivity } from '../utils/sessionIdle';

// Resolved auth result, cached at module scope. AuthWrapper wraps every route
// individually, so it remounts on each navigation; without this cache every
// navigation would blank the screen behind the loading fallback while the
// user's role is re-read from Firestore.
let cachedAuth = null; // { uid, role }

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: 'inherit',
};

const AuthWrapper = ({ children }) => {
  const currentUser = auth.currentUser;
  const hasCachedRole = Boolean(cachedAuth && currentUser && cachedAuth.uid === currentUser.uid);
  const [isLoading, setIsLoading] = useState(!hasCachedRole);
  const [user, setUser] = useState(currentUser); // Store user object
  const [role, setRole] = useState(hasCachedRole ? cachedAuth.role : null); // Store role from Firestore
  const navigate = useNavigate();
  const { setProfileImageUrl } = useUserProfile(); // Use setProfileImageUrl from context

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user); // Store the authenticated user

        // Already know this user's role from an earlier route: render now and
        // let the Firestore read below refresh it in the background.
        if (cachedAuth && cachedAuth.uid === user.uid) {
          setRole(cachedAuth.role);
          setIsLoading(false);
        }

        // Fetch the role and profile image from Firestore
        try {
          const docRef = doc(db, 'Users', user.uid); // Assuming your collection is 'Users'
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const userData = docSnap.data();
            setRole(userData.role); // Set the role from Firestore
            cachedAuth = { uid: user.uid, role: userData.role };

            // Fetch profile image URL and store it globally
            if (userData.profileImageUrl) {
              setProfileImageUrl(userData.profileImageUrl);
            }
          } else {
            console.log('No such document!');
          }
        } catch (error) {
          // Without this the loading fallback would never clear on a failed
          // read (offline, permission denied) and the page would hang blank.
          console.error('Failed to load user role:', error);
        }

        setIsLoading(false);
      } else {
        cachedAuth = null;
        navigate('/');
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, setProfileImageUrl]);

  // Hareketsizlik çıkışı: sınır ve son hareket utils/sessionIdle'da tutulur,
  // böylece belge okuyucu gibi ekranlar sınırı uzatabilir (useSessionHold)
  useEffect(() => {
    if (!user) return undefined;
    let lastMarked = 0;
    let signingOut = false;

    const onActivity = () => {
      // Throttle: localStorage'a en fazla saniyede bir yaz
      const now = Date.now();
      if (now - lastMarked < 1000) return;
      lastMarked = now;
      markActivity();
    };

    // Odak bir iframe'e (PDF/sunum görüntüleyici) geçtiyse kullanıcı onun
    // içinde çalışıyordur; oradaki olaylar bu belgeye hiç ulaşmaz
    const onBlur = () => {
      if (document.activeElement?.tagName === 'IFRAME') markActivity();
    };

    const checkIdle = async () => {
      if (signingOut || Date.now() - getLastActivity() < getIdleLimit()) return;
      signingOut = true;
      await signOut(auth);
      navigate('/');
    };

    // Use passive listeners and skip mousemove (mousedown is sufficient for click detection).
    // Capture: scroll kabarcıklanmaz; pencere/panel içindeki kaydırma da sayılsın
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, onActivity, { passive: true, capture: true });
    });
    window.addEventListener('blur', onBlur);

    // Giriş sayfası bu sarmalayıcının dışında; oradaki hareket sayılmadığı için
    // her açılışı (giriş, sayfa geçişi) hareket say
    markActivity();
    // Sayaç yerine düzenli kontrol: sınır açık okuyucuya göre değişebilir
    const checkTimer = setInterval(checkIdle, 15000);

    return () => {
      clearInterval(checkTimer);
      activityEvents.forEach(event => {
        document.removeEventListener(event, onActivity, { capture: true });
      });
      window.removeEventListener('blur', onBlur);
    };
  }, [user, navigate]);

  if (isLoading) {
    // Inherits the body background so it does not flash white over dark mode.
    return (
      <div style={loadingStyle}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  // Pass the userId and role to the children components
  return React.cloneElement(children, { userId: user?.uid, role });
};

export default AuthWrapper;
